import { computed, Ref } from 'vue';
import * as yup from 'yup';
import { Disbursement, ServiceType, ValidDisbursementStatusIds, Draw, DisbursementStatuses } from '@/models/loans';
import { ref } from 'vue';
import axios from 'axios';
import { $api } from '@/services/api1';

export interface ValidationData {
	selectedDisbursement: Ref<Disbursement | null>;
	mode: Ref<'view' | 'edit' | 'add'>;
	disbursementStatusOptions: Ref<ServiceType[]>;
	disbursementStatusReasonOptions: Ref<ServiceType[]>;
	drawId: Ref<number>;
	draw: Ref<Draw>;
	loanId: Ref<number>;
	disbursements: Ref<Disbursement[]>;
}

export async function validateDrawDisbursementDateAsync(loanId: number, drawId: number, disbursementDate: Date) {
	await axios.post(`/api/loanservice/loan/${loanId}/draw/${drawId}/disbursement/DisbursementDate`, { disbursementDate})
	.then((response) => response)
	.catch((error) => console.log('validateDisbursementDate Error:', error));
}

export function useDisbursementFormFields(data: ValidationData) {
		const keysToSkip = [
		'certifiedAmount',
		'disbursedAmount',
		'disbursementStatusTypeId',
		'drawDisbursementStatusReasonTypeId',
		'transactionDate',
		'transactionDateDisplay'
	];

	const keysToRename = [
		{ key: "sequenceNumber", rename: "Sequence" },
		{ key: "disbursementStatusTypeName", rename: "Disbursement Status" },
		{ key: "drawDisbursementStatusReasonTypeName", rename: "Disbursement Status Reason" },
		{ key: "transactionDateDisplay", rename: "Disbursement Date" },
		{ key: "certifiedAmountFormatted", rename: "Certified Amount" },
		{ key: "disbursedAmountFormatted", rename: "Disbursed Amount" },
		{ key: "disbursementDateDisplay", rename: "Disbursement Date" }
	];

	const holidays = ref();

	const formFields = computed(() => {
		const fields = [
			{
				name: 'sequenceNumber',
				label: 'Sequence',
				type: 'string',
				value: data.selectedDisbursement.value?.sequenceNumber,
				width: 'col-4',
				showOnEdit: true,
				disabledOnEdit: true,
				disabledOnAdd: true,
			},
			{
				name: 'disbursementStatusTypeId',
				label: 'Disbursement Status',
				type: 'select',
				value: data.selectedDisbursement.value?.disbursementStatusTypeId,
				width: 'col-4',
				options: getFilteredDisbursementStatuses(data).value,
				showOnEdit: true,
				disabledOnEdit: false
			},
			{
				name: 'drawDisbursementStatusReasonTypeId',
				label: 'Disbursement Status Reason',
				type: 'select',
				value: data.selectedDisbursement.value?.drawDisbursementStatusReasonTypeId,
				width: 'col-4',
				options: data.disbursementStatusReasonOptions.value,
				showOnEdit: true,
				disabledOnEdit: false
			},
			{
				name: 'transactionDate',
				label: 'Disbursement Date',
				type: 'date',
				value: data.selectedDisbursement.value?.transactionDate,
				width: 'col-4',
				showOnEdit: true,
				disabledOnEdit: true
			},
			{
				name: 'certifiedAmount',
				label: 'Certified Amount',
				type: 'number',
				value: data.selectedDisbursement.value?.certifiedAmount,
				width: 'col-4',
				showOnEdit: true,
				disabledOnEdit: true
			},
			{
				name: 'disbursedAmount',
				label: 'Disbursed Amount',
				type: 'number',
				value: data.selectedDisbursement.value?.disbursedAmount,
				width: 'col-4',
				showOnEdit: true,
				disabledOnEdit: false
			},
			{
				name: 'comment',
				label: 'Comment',
				type: 'string',
				value: '',
				width: 'col-12',
				showOnEdit: true,
				disabledOnEdit: false
			}
		];

		return fields.filter((field) => {
			if (data.mode.value === 'edit') {
				return field.showOnEdit !== false;
			}

			return true;
		});
	});

	const minTransactionDate = computed(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		
		return today;
	});

	const maxTransactionDate = computed(() => {
		return data.draw.value.loanPeriodEndDate
	});

	async function setHolidays(year: number) {
		holidays.value = await $api.types.getFederalHolidays(year);
	}

	const addSchema = computed(() => {
		return yup.object().shape({
			sequenceNumber: yup.string().required("Sequence is required"),
			disbursementStatusTypeId: yup
				.number()
				.typeError('Disbursement Status is invalid')
				.required('Disbursement Status is required')
				.min(1),
			drawDisbursementStatusReasonTypeId: yup
				.number()
				.nullable()
				.typeError('Disbursement Status Reason is invalid')
				.min(1)
				.when('disbursementStatusTypeId', {
					is: DisbursementStatuses.CertifiedOnHold,
					then: (schema) => schema.required('Status Reason is required'),
					otherwise: (schema) => schema.notRequired()
				}),
			transactionDate: yup
        .date()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .required("Disbursement Date is required")
				.min(minTransactionDate.value, 'Disbursement Date cannot be in the past')
				.max(maxTransactionDate.value, "Disbursement Date must not occur after the Draw Loan Period End Date")
				.test(
					'is-valid-disbursement-date',
					`Disbursement Date cannot be a weekend or a holiday`,
					async function (value, testContext) {
						if (!value) return true;
						const month = value.getMonth() + 1;
						const day = value.getDate();
						const dateStr = `${value.getFullYear()}/${month.toString().length < 2 ? "0"+month: month}/${day.toString().length < 2 ? "0"+day : day}`.replaceAll("/","-");

						await setHolidays(value.getFullYear());

						const isHoliday = holidays.value?.find((h: {name: string, date: string}) => h.date === dateStr);

						const isWeekend = value.getDay() === 6 || value.getDay() === 0;

						return !isHoliday && !isWeekend;
					}
				),
			certifiedAmount: yup.number().required("Certified Amount is required"),
			disbursedAmount: yup
				.number()
				.transform((curr, orig) => (orig === "" ? null : curr))
				.typeError('Disbursed Amount is invalid')
				.required('Disbursed Amount is required')
				.when('disbursementStatusTypeId', {
					is: DisbursementStatuses.PreDisbursementCancellation,
					then: (schema) => schema.min(0, 'Disbursed Amount must be $0').max(0),
					otherwise: (schema) => schema.min(50, 'Must be greater than $50')
				})
				.test('valid-disbursed-amount', 'Disbursed Amount is invalid', function (value, testContext) {
					const errors: yup.ValidationError[] = [];
					if (value === undefined || value === null || value < 0) {
						errors.push(
							testContext.createError({
								path: testContext.path,
								message: "Disbursement Amount is required",
							})
						);
					}
					else {
					const certifiedAmount = testContext.parent.certifiedAmount;
					const disbursementId = data.selectedDisbursement.value?.id || 0;

					// Gets the sum of all the existing disbursements' disbursed amount
					const totalDisbursedAmount = data.disbursements.value
						.filter((d: Disbursement) => d.id > 0 && d.id !== disbursementId)
						.reduce((accumulator, currentTransaction) => {
							const val = currentTransaction.disbursedAmount;
							return accumulator + (val || 0);
						}, 0); // The 0 is the initial value of the accumulator

					const newTotalAmount = totalDisbursedAmount + value;

					if (certifiedAmount) {
						if (value > certifiedAmount) {
							errors.push(
								testContext.createError({
									path: testContext.path,
									message: `Disbursed Amount cannot be greater than the draw certified amount`,
								})
							);
						}

						if (value <= certifiedAmount && newTotalAmount > certifiedAmount) {
							errors.push(
								testContext.createError({
									path: testContext.path,
									message: `The total disbursement amount cannot be greater than the draw certified amount.`,
								})
							);
						}
					} else {
						errors.push(
							testContext.createError({
								path: testContext.path,
								message: "Certified Amount is required to enter Disbursed Amount",
						}));
					}

					if (errors.length > 0) {
						throw new yup.ValidationError(errors);
					}

					return true;
				}		
			}),
			comment: yup
				.string()
				.max(4000)
				.typeError('Comment is invalid')
				.when('disbursementStatusTypeId', {
					is: DisbursementStatuses.CertifiedOnHold,
					then: (schema) => schema.required('Comment is required'),
					otherwise: (schema) => schema.notRequired()
				})
		})
	});

	const editSchema = computed(() => {
		return yup.object({
			disbursementStatusTypeId: yup
				.number()
				.typeError('Disbursement Status is invalid')
				.required('Disbursement Status is required')
				.min(1),
			drawDisbursementStatusReasonTypeId: yup
				.number()
				.nullable()
				.typeError('Disbursement Status Reason is invalid')
				.min(1)
				.when('disbursementStatusTypeId', {
					is: DisbursementStatuses.CertifiedOnHold,
					then: (schema) => schema.required('Disbursement Status Reason is required'),
					otherwise: (schema) => schema.notRequired()
				}),
			disbursedAmount: yup
				.number()
				.transform((curr, orig) => (orig === "" ? null : curr))
				.typeError('Disbursed Amount is invalid')
				.required('Disbursed Amount is required')
				.when('disbursementStatusTypeId', {
					is: DisbursementStatuses.PreDisbursementCancellation,
					then: (schema) => schema.min(0, 'Disbursed Amount must be $0').max(0),
					otherwise: (schema) => schema.min(50, 'Must be greater than $50')
				})
				.test('has-certified-amount', 'Certified Amount is required to enter Disbursed Amount', function () {
					return data.selectedDisbursement.value?.certifiedAmount !== undefined;
				})
				.test(
					'is-under-limit',
					`Disbursed Amount cannot exceed $${data.selectedDisbursement.value?.certifiedAmount}`,
					function (value) {
						const limit = data.selectedDisbursement.value?.certifiedAmount;
						return limit ? value <= limit : false;
					}
				),
			comment: yup
				.string()
				.nullable()
				.max(4000)
				.typeError('Comment is invalid')
				.when('disbursementStatusTypeId', {
					is: DisbursementStatuses.CertifiedOnHold,
					then: (schema) => schema.required('Comment is required'),
					otherwise: (schema) => schema.notRequired()
				})
		});
	});

	const validationSchema = computed(() => {
		switch (data.mode.value) {
			case 'edit':
				return editSchema.value;
			case 'add':
				return addSchema.value;
			default:
				return yup.object({});
		}
	});

	return {
		formFields,
		validationSchema,
		setHolidays,
		keysToSkip,
		keysToRename,
	};
}

function getFilteredDisbursementStatuses(data: ValidationData) {
	return computed(() => {
		if (data.mode.value === 'view') {
			return data.disbursementStatusOptions.value;
		}

		const filteredOptions = data.disbursementStatusOptions.value.filter((status) =>
			ValidDisbursementStatusIds.includes(status.id)
		);

		if (data.mode.value === 'edit' && data.selectedDisbursement.value?.disbursementStatusTypeId !== null) {
			const selectedOption = data.disbursementStatusOptions.value.find(
				(status) => status.id === data.selectedDisbursement.value?.disbursementStatusTypeId
			);

			if (selectedOption && !filteredOptions.includes(selectedOption)) {
				filteredOptions.push(selectedOption);
			}
		}

		return filteredOptions;
	});
}
